interface Pair {
    trigger: HTMLElement;
    panel: HTMLElement;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(panel: HTMLElement): HTMLElement[] {
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

function openPanel(trigger: HTMLElement, panel: HTMLElement): void {
    panel.classList.remove('is-closing');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    getFocusableElements(panel)[0]?.focus();

    window.dispatchEvent(new Event('resize'));
}

function closePanel(trigger: HTMLElement, panel: HTMLElement, animate = false): void {
    const wasOpen = panel.classList.contains('is-open');
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');

    if (!animate || !wasOpen) {
        panel.classList.remove('is-closing');
        return;
    }

    const host = panel.parentElement;
    const duration = host ? parseFloat(getComputedStyle(host).transitionDuration) * 1000 : 0;
    if (!host || !duration) return;

    panel.classList.add('is-closing');
    const finish = (): void => {
        window.clearTimeout(fallback);
        host.removeEventListener('transitionend', onTransitionEnd);
        panel.classList.remove('is-closing');
    };
    const onTransitionEnd = (e: TransitionEvent): void => {
        if (e.target === host && e.propertyName === 'grid-template-rows') finish();
    };
    // transitionend doesn't fire if the transition gets interrupted, e.g. by reopening
    const fallback = window.setTimeout(finish, duration + 50);
    host.addEventListener('transitionend', onTransitionEnd);
}

export function initFlyoutDesktopMenu(): void {
    const nav = document.querySelector<HTMLElement>('.flyout');
    if (!nav) return;

    const triggerList = nav.querySelector<HTMLElement>(':scope > ul');
    const panelHost = nav.querySelector<HTMLElement>('.flyout__secondLevel-host');
    const pairs: Pair[] = [];
    const closeAll = (): void => pairs.forEach(({ trigger, panel }) => closePanel(trigger, panel, true));

    nav.querySelectorAll<HTMLElement>('[aria-controls]').forEach((trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        pairs.push({ trigger, panel });

        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            const isOpen = panel.classList.contains('is-open');

            if (isOpen) {
                closePanel(trigger, panel, true);
                return;
            }
            pairs.forEach(({ trigger: t, panel: p }) => closePanel(t, p));
            openPanel(trigger, panel);
        });

        const closeBtn = panel.querySelector<HTMLButtonElement>('.flyout__secondLevel__close');
        closeBtn?.addEventListener('click', () => {
            closePanel(trigger, panel, true);
            trigger.focus();
        });
    });

    // Any click outside the trigger list and the panel closes the open panel - including the backdrop
    // and the empty area of the full-width nav around the trigger list
    document.addEventListener('click', (e) => {
        const target = e.target as Node;
        if (triggerList?.contains(target) || panelHost?.contains(target)) return;
        closeAll();
    });

    // Escape closes the open panel and returns focus to its trigger
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        const openPair = pairs.find(({ panel }) => panel.classList.contains('is-open'));
        closeAll();
        openPair?.trigger.focus();
    });

    // Tab order should read as: trigger, (its panel's content, if open), next trigger, ...
    // Panels live in a shared host after the trigger list for the height-transition trick, so
    // the DOM order alone doesn't produce that sequence — the four boundary jumps are handled here.
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        const openIndex = pairs.findIndex(({ panel }) => panel.classList.contains('is-open'));
        if (openIndex === -1) return;

        const { trigger: openTrigger, panel: activePanel } = pairs[openIndex];
        const focusable = getFocusableElements(activePanel);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const nextTrigger = pairs[openIndex + 1]?.trigger;
        const active = document.activeElement;

        if (!e.shiftKey && active === openTrigger) {
            e.preventDefault();
            first.focus();
        } else if (!e.shiftKey && active === last && nextTrigger) {
            e.preventDefault();
            nextTrigger.focus();
        } else if (e.shiftKey && active === first) {
            e.preventDefault();
            openTrigger.focus();
        } else if (e.shiftKey && active === nextTrigger) {
            e.preventDefault();
            last.focus();
        }
    });
}
